import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  getPassagensTicketTemplateMeta,
  previewPassagensTicketTemplate,
} from "../api/client";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import { useAuthContext } from "../context/AuthContext";
import { useS3Upload } from "../hooks/useS3Upload";
import { TICKET_LOGO, TICKET_LOGO_HINT, ticketLogoRejection } from "../constants/ticketLogoConstraints";

const KINDS = [
  {
    id: "bpe",
    fonteKey: "ticket_bpe_fonte",
    htmlKey: "ticket_bpe_html",
    title: "Bilhete eletrônico (BPe)",
    summary: "Documento fiscal. O passageiro embarca com o QR Code e a chave de acesso.",
  },
  {
    id: "voucher",
    fonteKey: "ticket_voucher_fonte",
    htmlKey: "ticket_voucher_html",
    title: "Comprovante (voucher)",
    summary:
      "Não vale para embarque. O passageiro leva este comprovante ao guichê para retirar o bilhete.",
  },
];

function fillPlaceholders(html, sample) {
  return String(html || "").replace(/%([A-Za-z][A-Za-z0-9_]*)%/g, (_, key) => {
    const v = sample?.[key];
    return v == null ? "" : String(v);
  });
}

function KindEditor({
  kind,
  form,
  onChange,
  meta,
  disabled,
  logoUrl,
}) {
  const fileRef = useRef(null);
  const [tab, setTab] = useState(0);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [validation, setValidation] = useState(null);

  const fonte = form[kind.fonteKey] === "proprio" ? "proprio" : "padrao";
  const html = form[kind.htmlKey] || "";
  const visibleHtml = String(html).replace(/<!--[\s\S]*?-->/g, "");
  const variables = meta?.variables || [];
  const groups = meta?.groups || [];
  const sample = meta?.sample?.[kind.id] || {};
  const defaultHtml = meta?.defaultHtml?.[kind.id] || "";
  const requiredKeys = meta?.required?.[kind.id] || [];
  const localMissing = fonte === "proprio"
    ? requiredKeys.filter((key) => !visibleHtml.includes(`%${key}%`))
    : [];

  const setFonte = (next) => onChange({ [kind.fonteKey]: next });
  const setHtml = (next) => onChange({ [kind.htmlKey]: next });

  const insertToken = (token) => {
    setHtml(`${html}${html && !html.endsWith("\n") ? "\n" : ""}${token}`);
  };

  const startFromDefault = () => {
    setHtml(defaultHtml);
    setFonte("proprio");
    setTab(0);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const name = String(file.name || "").toLowerCase();
    if (!name.endsWith(".html") && !name.endsWith(".htm") && file.type && !/html/.test(file.type)) {
      setPreviewError("Envie um arquivo .html");
      return;
    }
    if (file.size > (meta?.maxHtmlChars || 400000)) {
      setPreviewError("O arquivo é grande demais (máximo 400 KB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setHtml(String(reader.result || ""));
      setFonte("proprio");
      setPreviewError("");
    };
    reader.readAsText(file);
  };

  const openPreview = async () => {
    setTab(1);
    setPreviewing(true);
    setPreviewError("");
    try {
      const res = await previewPassagensTicketTemplate({
        kind: kind.id,
        fonte,
        html: fonte === "proprio" ? html : undefined,
        logoUrl: logoUrl || "",
      });
      setPreviewHtml(res?.filledHtml || fillPlaceholders(fonte === "proprio" ? html : defaultHtml, sample));
      setValidation(res?.validation || validation);
    } catch (err) {
      setPreviewError(err?.message || "Não foi possível montar a prévia.");
      setPreviewHtml(fillPlaceholders(fonte === "proprio" ? html : defaultHtml, sample));
    } finally {
      setPreviewing(false);
    }
  };

  const errors =
    validation?.errors ||
    localMissing.map((key) => ({
      code: "missing_variable",
      key,
      message: `Falta incluir: ${variables.find((v) => v.key === key)?.missingLabel || key}.`,
    }));
  const warnings = validation?.warnings || [];
  const requiredSet = new Set(requiredKeys);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="subtitle1" fontWeight={700}>
        {kind.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {kind.summary}
      </Typography>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={fonte}
        onChange={(_, v) => v && setFonte(v)}
        disabled={disabled}
        sx={{ mb: 1.5 }}
      >
        <ToggleButton value="padrao" sx={{ textTransform: "none", px: 1.5 }}>
          Modelo padrão do XPro
        </ToggleButton>
        <ToggleButton value="proprio" sx={{ textTransform: "none", px: 1.5 }}>
          Modelo da empresa
        </ToggleButton>
      </ToggleButtonGroup>

      {fonte === "padrao" ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Já segue o layout oficial, com os direitos do passageiro da ANTT. Só mude se a empresa
          tiver um HTML próprio.
        </Alert>
      ) : null}

      {fonte === "proprio" && errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
            Falta informação obrigatória neste modelo
          </Typography>
          {errors.map((e) => (
            <Typography key={e.code + (e.key || "")} variant="body2">
              • {e.message}
            </Typography>
          ))}
        </Alert>
      )}

      {fonte === "proprio" && warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {warnings.map((w) => (
            <Typography key={w.code} variant="body2">
              {w.message}
            </Typography>
          ))}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => (v === 1 ? openPreview() : setTab(0))}
        sx={{ mb: 1 }}
      >
        <Tab label={fonte === "proprio" ? "Editar modelo" : "Ver modelo"} />
        <Tab label="Ver como fica" />
      </Tabs>

      {tab === 0 && fonte === "padrao" && (
        <Typography variant="body2" color="text.secondary">
          Clique em <b>Ver como fica</b> para conferir o{" "}
          {kind.id === "voucher" ? "comprovante" : "bilhete"} com dados de exemplo. Se precisar do
          HTML da empresa, escolha <b>Modelo da empresa</b>.
        </Typography>
      )}

      {tab === 0 && fonte === "proprio" && (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <Button size="small" variant="outlined" onClick={() => fileRef.current?.click()} disabled={disabled}>
              Enviar arquivo HTML
            </Button>
            <Button size="small" variant="outlined" onClick={startFromDefault} disabled={disabled}>
              Começar pelo modelo padrão
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept=".html,.htm,text/html"
              hidden
              onChange={onFile}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Clique numa informação para incluir no modelo. O XPro preenche sozinho na hora da
            emissão.
          </Typography>
          {groups.map((g) => {
            const vars = variables.filter((v) => v.group === g.id);
            if (!vars.length) return null;
            return (
              <Box key={g.id} sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {g.label}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {vars.map((v) => (
                      <Tooltip key={v.key} title={`${v.label} — ${v.token}`}>
                        <Chip
                          size="small"
                          label={v.label}
                          color={requiredSet.has(v.key) ? "primary" : "default"}
                          variant={requiredSet.has(v.key) ? "filled" : "outlined"}
                          onClick={() => insertToken(v.token)}
                          disabled={disabled}
                        />
                      </Tooltip>
                  ))}
                </Stack>
              </Box>
            );
          })}
          <TextField
            label="Código do modelo (HTML)"
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            fullWidth
            multiline
            minRows={8}
            disabled={disabled}
            helperText="Pode colar o HTML e incluir as variáveis da lista acima (%Origem%, %ChaveBPe%…)."
            sx={{ mt: 1, "& textarea": { fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 } }}
          />
        </>
      )}

      {tab === 1 && (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
            bgcolor: "#f6f7f9",
            minHeight: 360,
            position: "relative",
          }}
        >
          {previewing && (
            <Stack alignItems="center" justifyContent="center" sx={{ position: "absolute", inset: 0, zIndex: 1 }}>
              <CircularProgress size={28} />
            </Stack>
          )}
          {previewError ? (
            <Alert severity="warning" sx={{ m: 1 }}>
              {previewError}
            </Alert>
          ) : null}
          <iframe
            title={`preview-${kind.id}`}
            sandbox=""
            srcDoc={previewHtml || "<p style='padding:16px;font-family:Arial'>Montando prévia…</p>"}
            style={{ width: "100%", height: 520, border: 0, background: "#fff" }}
          />
        </Box>
      )}
    </Paper>
  );
}

function readImageSize(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      URL.revokeObjectURL(url);
      resolve({ width, height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Não foi possível ler a imagem."));
    };
    img.src = url;
  });
}

function LogoPreview({ src, emptyLabel = "Sem logo no cadastro da empresa" }) {
  return (
    <Box
      sx={{
        width: 148,
        minHeight: 72,
        flexShrink: 0,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "#f6f7f9",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        px: 1,
        py: 1,
      }}
    >
      {src ? (
        <img src={src} alt="" style={{ width: 120, maxHeight: 48, objectFit: "contain" }} />
      ) : (
        <Stack alignItems="center" spacing={0.25}>
          <ImageRoundedIcon color="disabled" fontSize="small" />
          <Typography variant="caption" color="text.secondary" textAlign="center">
            {emptyLabel}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

function TicketLogoCard({ form, onChange, meta, disabled }) {
  const inputRef = useRef(null);
  const { userData } = useAuthContext();
  const { uploadOne } = useS3Upload();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [customizing, setCustomizing] = useState(() => Boolean(String(form.ticket_logo || "").trim()));

  const override = String(form.ticket_logo || "").trim();
  const companyUrl = String(meta?.logo?.companyUrl || "").trim();

  useEffect(() => {
    if (override) setCustomizing(true);
  }, [override]);

  const pickFile = () => {
    if (disabled || busy) return;
    inputRef.current?.click();
  };

  const resetToCompanyLogo = () => {
    setCustomizing(false);
    setError("");
    if (override) onChange({ ticket_logo: "" });
  };

  const applyFile = async (file) => {
    if (!file) return;
    setError("");
    try {
      const { width, height } = await readImageSize(file);
      const reason = ticketLogoRejection({
        type: file.type,
        size: file.size,
        width,
        height,
        name: file.name,
      });
      if (reason) {
        setError(reason);
        return;
      }
      setBusy(true);
      const prefix = `workspace-${userData?.id_workspace || "default"}/passagens-ticket-logo/${new Date().getFullYear()}`;
      const { url } = await uploadOne(file, { prefix });
      onChange({ ticket_logo: url || "" });
    } catch (e) {
      setError(e?.message || "Não foi possível enviar a imagem.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="subtitle1" fontWeight={700}>
        Logo no documento
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
        Já usamos a logo do cadastro da empresa. Só personalize aqui se este documento precisar de
        outra imagem.
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ mb: 1.5 }}>
        <LogoPreview src={customizing && override ? override : companyUrl} />
        <Box>
          <Chip
            size="small"
            label={
              customizing && override
                ? "Logo só deste documento"
                : companyUrl
                  ? "Logo do cadastro da empresa"
                  : "Cadastro da empresa sem logo"
            }
            sx={{ mb: 0.75 }}
          />
          <Typography variant="body2" color="text.secondary">
            {customizing && override
              ? "Esta imagem vale só para este documento."
              : companyUrl
                ? "O passageiro vê esta logo automaticamente."
                : "Sem logo na empresa, o bilhete sai com o nome da transportadora."}
          </Typography>
        </Box>
      </Stack>

      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
        Quer personalizar a logo só neste documento?
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={customizing ? "sim" : "nao"}
        onChange={(_, v) => {
          if (!v || disabled) return;
          if (v === "nao") resetToCompanyLogo();
          else {
            setCustomizing(true);
            setError("");
          }
        }}
        disabled={disabled}
        sx={{ mb: customizing ? 1.5 : 0 }}
      >
        <ToggleButton value="nao" sx={{ textTransform: "none", px: 1.5 }}>
          Não, usar a da empresa
        </ToggleButton>
        <ToggleButton value="sim" sx={{ textTransform: "none", px: 1.5 }}>
          Sim, personalizar
        </ToggleButton>
      </ToggleButtonGroup>

      {customizing ? (
        <Box
          sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid", borderColor: "divider" }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!disabled && !busy) applyFile(e.dataTransfer?.files?.[0]);
          }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            Envie a imagem que deve aparecer neste documento.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {TICKET_LOGO_HINT} No documento ela aparece com 120 px de largura.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Button
              size="small"
              variant="outlined"
              startIcon={busy ? <CircularProgress size={14} /> : <UploadRoundedIcon />}
              disabled={disabled || busy}
              onClick={pickFile}
              sx={{ textTransform: "none" }}
            >
              {override ? "Trocar imagem" : "Selecionar imagem"}
            </Button>
            {override ? (
              <Button size="small" disabled={disabled || busy} onClick={resetToCompanyLogo} sx={{ textTransform: "none" }}>
                Voltar a usar a da empresa
              </Button>
            ) : null}
          </Stack>
          {error ? (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {error}
            </Alert>
          ) : null}
        </Box>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        hidden
        accept={TICKET_LOGO.mimeTypes.join(",")}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          applyFile(file);
        }}
      />
    </Paper>
  );
}

export default function BpeTicketTemplateEditor({
  form,
  onChange,
  disabled,
  visibleKind = "bpe",
}) {
  const [meta, setMeta] = useState(null);
  const [loadError, setLoadError] = useState("");
  const activeKind = KINDS.find((k) => k.id === visibleKind) || KINDS[0];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getPassagensTicketTemplateMeta();
        if (!cancelled) setMeta(data);
      } catch (err) {
        if (!cancelled) setLoadError(err?.message || "Não foi possível carregar o catálogo do bilhete.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const patch = (partial) => onChange((prev) => ({ ...prev, ...partial }));
  const isVoucher = activeKind.id === "voucher";

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" fontWeight={700}>
        {isVoucher ? "Como o passageiro vê o comprovante" : "Como o passageiro vê o bilhete"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isVoucher
          ? "Este comprovante não vale para embarque. O padrão já avisa que o bilhete deve ser retirado no guichê. Só envie um HTML próprio se a empresa tiver layout específico."
          : "Modelo do bilhete eletrônico. O padrão já atende a ANTT. Só envie um HTML próprio se a empresa tiver layout específico."}
      </Typography>
      {loadError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      ) : null}
      {!meta && !loadError ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Carregando modelos…
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <TicketLogoCard form={form} onChange={patch} meta={meta} disabled={disabled} />
          <KindEditor
            key={activeKind.id}
            kind={activeKind}
            form={form}
            onChange={patch}
            meta={meta}
            disabled={disabled}
            logoUrl={form.ticket_logo || ""}
          />
        </Stack>
      )}
      <Divider sx={{ mt: 2 }} />
    </Box>
  );
}

export function findTicketTemplateSaveBlockers(form, validations = {}, visibleKind = "bpe") {
  const blockers = [];
  for (const kind of KINDS) {
    if (visibleKind && kind.id !== visibleKind) continue;
    if (form[kind.fonteKey] !== "proprio") continue;
    const v = validations[kind.id];
    if (v && v.canSave === false) {
      blockers.push(
        `O ${kind.title.toLowerCase()} ainda está incompleto. Veja o que falta acima.`
      );
    }
  }
  return blockers;
}

export { KINDS as TICKET_TEMPLATE_KINDS };
